/**
 * Pull-to-refresh for the KayiCom Android WebView shell.
 * Native SwipeRefreshLayout alone misses many SPA pages (inner scrollers).
 * This gesture works on every route when the page is scrolled to the top.
 */
export const installAppPullToRefresh = () => {
  if (typeof window === 'undefined') return () => {};
  // Native shell may already have injected a fallback — don't double-bind.
  if (window.__kayiPtrInstalled || window.__kayiPtrNativeFallback) {
    window.__kayiPtrInstalled = true;
    return () => {};
  }
  window.__kayiPtrInstalled = true;

  let startY = 0;
  let tracking = false;
  let lastTrigger = 0;

  const menuOpen = () => {
    try {
      return document.body.style.overflow === 'hidden';
    } catch {
      return false;
    }
  };

  const atTop = () => {
    if (menuOpen()) return false;
    const y = window.scrollY || window.pageYOffset || 0;
    const se = document.scrollingElement || document.documentElement;
    if (y > 4 || (se && se.scrollTop > 4) || (document.body && document.body.scrollTop > 4)) {
      return false;
    }
    try {
      const nodes = document.querySelectorAll(
        'main, [data-scroll], .overflow-y-auto, .overflow-auto, .overflow-y-scroll'
      );
      for (let i = 0; i < nodes.length; i += 1) {
        const el = nodes[i];
        if (el.scrollTop > 4 && el.scrollHeight > el.clientHeight + 40) {
          return false;
        }
      }
    } catch {
      // ignore
    }
    return true;
  };

  const syncNative = () => {
    try {
      if (window.KayiNative && typeof window.KayiNative.setPageAtTop === 'function') {
        window.KayiNative.setPageAtTop(atTop());
      }
    } catch {
      // ignore
    }
  };

  const triggerRefresh = () => {
    const now = Date.now();
    if (now - lastTrigger < 1200) return;
    lastTrigger = now;
    try {
      if (window.KayiNative && typeof window.KayiNative.pullRefresh === 'function') {
        window.KayiNative.pullRefresh();
        return;
      }
    } catch {
      // fall through
    }
    try {
      window.location.reload();
    } catch {
      // ignore
    }
  };

  const onTouchStart = (e) => {
    if (!e.touches || !e.touches.length || !atTop()) {
      tracking = false;
      return;
    }
    startY = e.touches[0].clientY;
    tracking = true;
  };

  const onTouchMove = (e) => {
    if (!tracking || !e.touches || !e.touches.length) return;
    const dy = e.touches[0].clientY - startY;
    // Pull down from top (~70px) → refresh on any page.
    if (dy > 70 && atTop()) {
      tracking = false;
      triggerRefresh();
    }
  };

  const onTouchEnd = () => {
    tracking = false;
  };

  window.addEventListener('scroll', syncNative, true);
  document.addEventListener('touchstart', onTouchStart, { passive: true });
  document.addEventListener('touchmove', onTouchMove, { passive: true });
  document.addEventListener('touchend', onTouchEnd, { passive: true });
  document.addEventListener('touchcancel', onTouchEnd, { passive: true });
  syncNative();
  const syncTimer = window.setInterval(syncNative, 1200);

  return () => {
    window.clearInterval(syncTimer);
    window.removeEventListener('scroll', syncNative, true);
    document.removeEventListener('touchstart', onTouchStart);
    document.removeEventListener('touchmove', onTouchMove);
    document.removeEventListener('touchend', onTouchEnd);
    document.removeEventListener('touchcancel', onTouchEnd);
    window.__kayiPtrInstalled = false;
  };
};
